import { usePathname } from 'next/navigation';
import { FC, useRef } from 'react';
import { Scrollbar } from 'src/common/components/Scrollbar';
import { useSettings } from 'src/common/hooks/useSettings';
import { LayoutType } from 'src/common/types/layout';
import type { NavColor } from 'src/common/types/settings';
import ProjectSelector from 'src/modules/projects/components/common/project-selector';

import { HomeButton } from '@/components/buttons/HomeButton';
import { Sheet, SheetContent } from '@/design-system/components/ui';
import { cn } from '@/design-system/lib/utils';

import type { Section } from '../config';
import { ProductRail } from '../vertical-layout/product-rail';
import { MobileNavSection } from './mobile-nav-section';

const MOBILE_NAV_WIDTH = 280;

const getCssVars = (color: NavColor, isDark: boolean): Record<string, string> => {
  switch (color) {
    case 'blend-in':
    case 'discreet':
      if (isDark) {
        return {
          '--nav-bg': '#0B0F19',
          '--nav-color': '#F3F4F6',
          '--nav-logo-border': '#2F3746',
          '--nav-section-title-color': '#9DA4AE',
          '--nav-item-color': '#9DA4AE',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': '#EDF2F7',
          '--nav-item-disabled-color': '#4D5761',
          '--nav-item-icon-color': '#6C737F',
          '--nav-item-icon-active-color': '#4C6EF5',
          '--nav-item-icon-disabled-color': '#2F3746',
          '--nav-item-chevron-color': '#2F3746',
          '--nav-scrollbar-color': '#9DA4AE',
        };
      }
      return {
        '--nav-bg': '#F8F9FA',
        '--nav-color': '#111927',
        '--nav-logo-border': '#F3F4F6',
        '--nav-section-title-color': '#9DA4AE',
        '--nav-item-color': '#6C737F',
        '--nav-item-hover-bg': 'rgba(17, 25, 39, 0.04)',
        '--nav-item-active-bg': 'rgba(17, 25, 39, 0.12)',
        '--nav-item-active-color': '#111927',
        '--nav-item-disabled-color': '#9DA4AE',
        '--nav-item-icon-color': '#9DA4AE',
        '--nav-item-icon-active-color': '#4C6EF5',
        '--nav-item-icon-disabled-color': '#9DA4AE',
        '--nav-item-chevron-color': '#9DA4AE',
        '--nav-scrollbar-color': '#111927',
      };

    case 'evident':
    default:
      return {
        '--nav-bg': '#1C2536',
        '--nav-color': '#ffffff',
        '--nav-logo-border': '#2F3746',
        '--nav-section-title-color': '#9DA4AE',
        '--nav-item-color': '#9DA4AE',
        '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
        '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
        '--nav-item-active-color': '#ffffff',
        '--nav-item-disabled-color': '#6C737F',
        '--nav-item-icon-color': '#9DA4AE',
        '--nav-item-icon-active-color': '#4C6EF5',
        '--nav-item-icon-disabled-color': '#6C737F',
        '--nav-item-chevron-color': '#4D5761',
        '--nav-scrollbar-color': '#9DA4AE',
      };
  }
};

interface MobileNavProps {
  color?: NavColor;
  onClose?: () => void;
  open?: boolean;
  sections?: Section[];
  layoutType?: LayoutType;
}

export const MobileNav: FC<MobileNavProps> = (props) => {
  const { color = 'evident', open, onClose, sections = [] } = props;
  const pathname = usePathname();
  const settings = useSettings();
  const isDark = settings.paletteMode === 'dark';
  const cssVars = getCssVars(color, isDark);
  const projectSelectorContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Sheet open={!!open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <SheetContent
        side="left"
        className="tw:p-0 tw:border-none"
        style={{
          ...(cssVars as React.CSSProperties),
          backgroundColor: 'var(--nav-bg)',
          color: 'var(--nav-color)',
          width: MOBILE_NAV_WIDTH,
          maxWidth: MOBILE_NAV_WIDTH,
        }}
      >
        <div className="tw:flex tw:flex-row tw:h-full">
          {props.layoutType === LayoutType.PROJECT && <ProductRail />}
          <Scrollbar style={{ height: '100%', flex: 1 }}>
            <div className="tw:flex tw:flex-col tw:h-full">
              {props.layoutType !== LayoutType.PROJECT && (
                <div className="tw:flex tw:flex-row tw:items-center tw:p-6 tw:gap-4">
                  <HomeButton />
                </div>
              )}
              <nav
                className={cn(
                  'tw:flex tw:flex-col tw:gap-4 tw:flex-1 tw:px-2',
                  props.layoutType === LayoutType.PROJECT && 'tw:pt-5',
                )}
              >
                {sections.map((section, index) => (
                  <MobileNavSection
                    items={section.items}
                    key={index}
                    pathname={pathname}
                    subheader={section.subheader}
                  />
                ))}
              </nav>
              {props.layoutType === LayoutType.PROJECT && (
                <div ref={projectSelectorContainerRef} className="tw:px-2 tw:flex tw:flex-col tw:gap-8 tw:relative">
                  <ProjectSelector portalContainerRef={projectSelectorContainerRef} />
                </div>
              )}
            </div>
          </Scrollbar>
        </div>
      </SheetContent>
    </Sheet>
  );
};
